import { ANIMATION_DATA_VERSION, type AnimationData, STAGE_IDS } from './types';

import { ImageGenerationRequestModelEnum, VideoGenerationRequestModelEnum } from 'js/src/client';

export const GENX_PLACEHOLDER_TEMPLATE_ID = 'assembly';

// Shown when a team has opted out of an AI feature that GenX (animated explainers)
// depends on. Used both for the disabled entry-button tooltips and the redirect toast.
export const GENX_UNAVAILABLE_MESSAGE =
    'Animated explainers are not available on your account. Contact your team administrator.';

export const DEFAULT_TARGET_DURATION_SECONDS = 30;

// Mirror GENX_FREE/PAID_MAX_SCRIPT_DURATION_SECONDS in shared/enums.py. FREE is used
// as a safe fallback when server capabilities are unavailable; PAID is the value the
// capability reports for paid accounts (also the largest pickable target duration).
// The authoritative cap is SERVER.USER.capabilities.genx_max_script_duration_seconds.
export const GENX_FREE_MAX_SCRIPT_DURATION_SECONDS = 30;
export const GENX_PAID_MAX_SCRIPT_DURATION_SECONDS = 90;
export const FIRST_STAGE_ID = STAGE_IDS.DESCRIBE_STYLE;

export const AUTOSAVE_INTERVAL_MS = 40 * 1000;

export const FRESH_ANIMATION_DATA: AnimationData = {
    version: ANIMATION_DATA_VERSION,
    isAnimationEditorActive: true,
    shots: [],
    styleReferenceAssets: [],
    selectedVoiceSlug: null,
    selectedCaptionsSceneDesignId: null,
    captionsFontIndex: null,
    captionsColorOverrides: null,
    captionsTextSize: null,
    selectedTemplateId: null,
    contentSource: 'describe',
    targetDuration: DEFAULT_TARGET_DURATION_SECONDS,
    activeStageId: FIRST_STAGE_ID,
};

// Long-tail latency mitigations for AI image generation (AP-514). Image
// providers occasionally take many minutes vs. their ~30s norm; these caps stop
// a slow generation from blocking the user.
//   - Derived (auto) ingredient reference images are best-effort: on timeout we
//     drop the ingredient and move on.
//   - Scene images are required: on timeout we fail the generation so the user
//     gets the "failed" view and can retry.
export const DERIVED_INGREDIENT_GENERATION_TIMEOUT_MS = 45 * 1000;
export const SCENE_IMAGE_GENERATION_TIMEOUT_MS = 120 * 1000;

// Video generation is far slower than image generation — fal's p99 runs ~13 min and
// the tail spikes past the websocket client's default 10-min wait, so successful-but-slow
// generations were being dropped as client-side timeouts. Give video its own longer ceiling.
// 15 min clears the observed p99 with margin while still bounding how long a user can wait.
export const VIDEO_GENERATION_TIMEOUT_MS = 15 * 60 * 1000;

export const DEFAULT_IMAGE_MODEL = ImageGenerationRequestModelEnum.FAL_AI_NANO_BANANA_2_EDIT;
export const DEFAULT_VIDEO_MODEL =
    VideoGenerationRequestModelEnum.FAL_AI_KLING_VIDEO_O3_PRO_REFERENCE_TO_VIDEO;

// "Fast" mode swaps video generation to Kling 3.0 Standard — cheaper and
// faster than the pro reference model that powers the default "quality" mode.
export const FAST_VIDEO_MODEL =
    VideoGenerationRequestModelEnum.FAL_AI_KLING_VIDEO_O3_STANDARD_REFERENCE_TO_VIDEO;

export type VideoQualityMode = 'fast' | 'quality';
export const DEFAULT_VIDEO_QUALITY_MODE: VideoQualityMode = 'quality';
